import Color from 'color';
import memoize from 'lodash/memoize';

import {
  FADE_OPACITY,
  GRID_BACKGROUND_COLOR,
  NODE_FULL_SIZE_SCALE,
  NODE_SMALL_SIZE_SCALE,
} from 'src/containers/ProjectDiagram/constants/styles';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  getDeletedModelConceptClassValue,
  isDeletedModelConceptClassKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createDeletedModelConceptClass';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

const whiteColor = Color('white');

type ColorParam = ConstructorParameters<typeof Color>[0];

const getClassIriFromKey = (key: string) =>
  isDeletedModelConceptClassKey(key)
    ? getDeletedModelConceptClassValue(key)
    : getModelConceptClassValue(key);

export const lightenWithoutTransparency = memoize(
  (color: ColorParam, opacity: number) => {
    return Color(color)
      .mix(whiteColor, 1 - opacity)
      .toString();
  }
);

const hiddenModelSelectedStyle = memoize((color: ColorParam) => {
  return Color(color).mix(whiteColor, 0.6).toString();
});

const hiddenModelUnselectedStyle = memoize((color: ColorParam) => {
  return Color(color).mix(whiteColor, 0.4).toString();
});

export const determineValueFromDraft =
  <T>(
    draftCb: (modelData: ModelData, obj: go.Shape | go.TextBlock) => boolean,
    defaultValue: T,
    draftValue?: T
  ) =>
  (modelData: ModelData, obj: go.Shape | go.TextBlock) => {
    return draftValue !== undefined &&
      modelData.draftData &&
      draftCb?.(modelData, obj)
      ? draftValue
      : defaultValue;
  };

export const determineColorFromSelection =
  (
    draftCb: (modelData: ModelData, obj: go.Shape | go.TextBlock) => boolean,
    defaultColor: ColorParam,
    draftColor?: ColorParam
  ) =>
  (modelData: ModelData, obj: go.Shape | go.TextBlock) => {
    const color = determineValueFromDraft(
      draftCb,
      defaultColor,
      draftColor
    )(modelData, obj) as string;
    const objPart = obj.part;
    const objData = objPart?.data;

    const hiddenModels = modelData.hiddenModelIds;
    const isClass = isModelConceptClass(objData);
    if (isClass && hiddenModels?.length) {
      const iri = objData.value;

      const models = modelData.modelsByIri[iri];
      const small = models?.every((model) => hiddenModels?.includes(model));

      if (small) {
        const isSelected = Boolean(!objPart?.isSelected);
        // Color is never transparent for a class
        const selectedColor = isSelected
          ? hiddenModelSelectedStyle(color)
          : hiddenModelUnselectedStyle(color);
        return selectedColor;
      }
    }

    return color !== 'transparent' &&
      color !== GRID_BACKGROUND_COLOR &&
      modelData.selection.length &&
      !objPart?.isSelected
      ? lightenWithoutTransparency(color, FADE_OPACITY)
      : color;
  };

export const determineOpacityFromSelection =
  () => (modelData: ModelData, obj: go.Shape | go.TextBlock) => {
    return modelData.selection.length && !obj.part?.isSelected
      ? FADE_OPACITY
      : 1.0;
  };

export const isRelationshipVisible = (
  modelData: ModelData,
  shape: go.Shape
) => {
  const hiddenModels = modelData.hiddenModelIds;
  if (!hiddenModels.length) {
    return true;
  }

  const objPart = shape.part;
  const objData = objPart?.data;

  const iri = objData?.value;
  const models = modelData.modelsByIri[iri];
  const isHidden = models?.every((model) => hiddenModels?.includes(model));

  return !isHidden;
};

export const isHierarchyVisible = (modelData: ModelData, shape: go.Shape) => {
  const hiddenModels = modelData.hiddenModelIds;
  if (!hiddenModels.length) {
    return true;
  }

  const objPart = shape.part;
  const objData = objPart?.data;

  const toKey = objData.to;
  const toIri = toKey ? getClassIriFromKey(toKey) : null;
  const toModels = toIri ? modelData.modelsByIri[toIri] : [];
  const isHiddenTo =
    toModels.length > 0 &&
    toModels.every((model) => hiddenModels?.includes(model));

  const fromKey = objData.from;
  const fromIri = fromKey ? getClassIriFromKey(fromKey) : null;
  const fromModels = fromIri ? modelData.modelsByIri[fromIri] : [];
  const isHiddenFrom =
    fromModels.length > 0 &&
    fromModels.every((model) => hiddenModels?.includes(model));

  return !(isHiddenTo || isHiddenFrom);
};

export const isClassScaled = (modelData: ModelData, shape: go.Shape) => {
  const hiddenModels = modelData.hiddenModelIds;
  if (!hiddenModels.length) {
    return NODE_FULL_SIZE_SCALE;
  }

  const objPart = shape.part;
  const objData = objPart?.data;

  const iri = objData?.value;
  const models = modelData.modelsByIri[iri];
  const small = models?.every((model) => hiddenModels?.includes(model));

  return small ? NODE_SMALL_SIZE_SCALE : NODE_FULL_SIZE_SCALE;
};

export const isMappingVisible = (modelData: ModelData, shape: go.Shape) => {
  const hiddenModels = modelData.hiddenModelIds;
  if (!hiddenModels.length) {
    return true;
  }

  const objPart = shape.part;
  const objData = objPart?.data;

  const toKey = objData.to;
  const toIri = getClassIriFromKey(toKey);
  const models = modelData.modelsByIri[toIri];
  const isHidden = models?.every((model) => hiddenModels?.includes(model));

  return !isHidden;
};

export const isClassVisible = (modelData: ModelData, shape: go.Shape) => {
  const hiddenModels = modelData.hiddenModelIds;
  if (!hiddenModels.length) {
    return true;
  }

  const objPart = shape.part;
  const objData = objPart?.data;

  const iri = objData?.value;
  const classModels = modelData.modelsByIri[iri];
  const hiddenClass = classModels?.every((model) =>
    hiddenModels?.includes(model)
  );
  if (!hiddenClass) {
    return true;
  }

  // findLinksConnected is in the docs, not sure about the type issue
  // @ts-ignore
  const links = shape?.findLinksConnected();
  let hasVisibleRelationship = false;
  links.each((link: go.Link) => {
    if (hasVisibleRelationship) {
      return;
    }
    const linkData = link?.data;
    if (isModelConceptRelationship(linkData)) {
      const relationshipIri = linkData?.value;
      const relationshipModels = modelData.modelsByIri[relationshipIri];
      const hiddenRelationship = relationshipModels?.every((model) =>
        hiddenModels?.includes(model)
      );
      if (!hiddenRelationship) {
        hasVisibleRelationship = true;
      }
    }
  });

  return hasVisibleRelationship;
};
